import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const CYAN = '#00f5ff';
const PURPLE = '#a855f7';

export default function HeroCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let alive = true;
    const cy = new THREE.Color(CYAN);
    const pu = new THREE.Color(PURPLE);

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 120);
    camera.position.set(0, 0.4, 7.2);

    const group = new THREE.Group();
    scene.add(group);

    const outer = new THREE.Mesh(
      new THREE.IcosahedronGeometry(2.05, 1),
      new THREE.MeshBasicMaterial({ color: cy, wireframe: true, transparent: true, opacity: 0.55 })
    );
    group.add(outer);

    const inner = new THREE.Mesh(
      new THREE.OctahedronGeometry(1.1, 0),
      new THREE.MeshBasicMaterial({ color: pu, wireframe: true, transparent: true, opacity: 0.7 })
    );
    group.add(inner);

    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(2.6, 32, 24),
      new THREE.MeshBasicMaterial({ color: cy, transparent: true, opacity: 0.045, side: THREE.BackSide })
    );
    group.add(halo);

    const particleCount = 900;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i += 1) {
      positions[i * 3] = (Math.random() - 0.5) * 34;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 24;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const dust = new THREE.Points(
      dustGeo,
      new THREE.PointsMaterial({ color: 0xffffff, size: 0.035, transparent: true, opacity: 0.5, sizeAttenuation: true })
    );
    scene.add(dust);

    const grid = new THREE.GridHelper(70, 56, cy.getHex(), pu.getHex());
    grid.material.transparent = true;
    grid.material.opacity = 0.14;
    grid.position.set(0, -4.6, 0);
    scene.add(grid);

    const resize = () => {
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener('resize', resize);

    const target = { x: 0, y: 0 };
    const onMove = (e) => {
      target.x = (e.clientX / window.innerWidth - 0.5) * 2;
      target.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    let raf;
    let t = 0;
    let mx = 0;
    let my = 0;
    const loop = () => {
      if (!alive) return;
      t += 0.006 * 1.4;
      mx += (target.x - mx) * 0.045;
      my += (target.y - my) * 0.045;
      group.rotation.y = t + mx * 0.4;
      group.rotation.x = Math.sin(t * 0.6) * 0.18 - my * 0.28;
      group.position.y = Math.sin(t * 1.1) * 0.16;
      inner.rotation.y = -t * 2.1;
      inner.rotation.z = t * 0.9;
      dust.rotation.y = t * 0.14;
      grid.position.z = ((t * 4) % 2.5) - 1.25;
      camera.position.x = mx * 0.5;
      camera.position.y = 0.4 - my * 0.35;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      renderer.dispose();
      outer.geometry.dispose();
      outer.material.dispose();
      inner.geometry.dispose();
      inner.material.dispose();
      halo.geometry.dispose();
      halo.material.dispose();
      dustGeo.dispose();
      dust.material.dispose();
      grid.material.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.9 }}
    />
  );
}
